import { multiply, repeatable, specialization, tech, unlock } from "./helpers.js";
import type {
  RepeatableDefinition,
  SpecializationDefinition,
  TechnologyDefinition,
} from "./types.js";

export const TECHNOLOGIES = [
  tech(
    "factory-automation",
    "Factory Automation",
    null,
    60,
    20,
    unlock("rig-factory", "yard-factory"),
  ),
  tech("factory-works", "Factory Works", "factory-automation", 600, 60, unlock("factory-works")),
  tech("better-drills-1", "Better Drills I", null, 80, 20, multiply("1.25", "mining-rig-output")),
  tech(
    "better-drills-2",
    "Better Drills II",
    "better-drills-1",
    240,
    30,
    multiply("1.25", "mining-rig-output"),
  ),
  tech(
    "better-drills-3",
    "Better Drills III",
    "better-drills-2",
    720,
    45,
    multiply("1.25", "mining-rig-output"),
  ),
  tech(
    "construction-tools-1",
    "Construction Tools I",
    null,
    80,
    20,
    multiply("1.25", "construction-yard-output"),
  ),
  tech(
    "construction-tools-2",
    "Construction Tools II",
    "construction-tools-1",
    240,
    30,
    multiply("1.25", "construction-yard-output"),
  ),
  tech(
    "construction-tools-3",
    "Construction Tools III",
    "construction-tools-2",
    720,
    45,
    multiply("1.25", "construction-yard-output"),
  ),
  tech(
    "faster-factory-lines-1",
    "Faster Factory Lines I",
    "factory-automation",
    160,
    30,
    multiply("1.25", "producer-factory-output", "producer-factory-metal-demand"),
  ),
  tech(
    "faster-factory-lines-2",
    "Faster Factory Lines II",
    "faster-factory-lines-1",
    480,
    45,
    multiply("1.25", "producer-factory-output", "producer-factory-metal-demand"),
  ),
  tech("faster-assembly-1", "Faster Assembly I", null, 120, 25, multiply("1.25", "assembly-work")),
  tech(
    "faster-assembly-2",
    "Faster Assembly II",
    "faster-assembly-1",
    360,
    40,
    multiply("1.25", "assembly-work"),
  ),
  tech(
    "efficient-generators-1",
    "Efficient Generators I",
    null,
    100,
    25,
    multiply("1.25", "power-generator-output"),
  ),
  tech(
    "efficient-generators-2",
    "Efficient Generators II",
    "efficient-generators-1",
    300,
    40,
    multiply("1.25", "power-generator-output"),
  ),
  tech(
    "efficient-generators-3",
    "Efficient Generators III",
    "efficient-generators-2",
    900,
    60,
    multiply("1.25", "power-generator-output"),
  ),
  tech(
    "lab-instruments-1",
    "Lab Instruments I",
    null,
    120,
    25,
    multiply("1.25", "research-lab-output"),
  ),
  tech(
    "lab-instruments-2",
    "Lab Instruments II",
    "lab-instruments-1",
    360,
    40,
    multiply("1.25", "research-lab-output"),
  ),
  tech("siege-guns", "Siege Guns", null, 120, 30, unlock("siege-gun")),
  tech(
    "robot-weapons-1",
    "Robot Weapons I",
    null,
    100,
    25,
    multiply("1.20", "guard-troop-damage", "raider-troop-damage"),
  ),
  tech(
    "robot-weapons-2",
    "Robot Weapons II",
    "robot-weapons-1",
    300,
    40,
    multiply("1.20", "guard-troop-damage", "raider-troop-damage"),
  ),
  tech(
    "robot-weapons-3",
    "Robot Weapons III",
    "robot-weapons-2",
    900,
    60,
    multiply("1.20", "guard-troop-damage", "raider-troop-damage"),
  ),
  tech(
    "reinforced-robots-1",
    "Reinforced Robots I",
    null,
    100,
    25,
    multiply("1.20", "guard-integrity", "raider-integrity"),
  ),
  tech(
    "reinforced-robots-2",
    "Reinforced Robots II",
    "reinforced-robots-1",
    300,
    40,
    multiply("1.20", "guard-integrity", "raider-integrity"),
  ),
  tech(
    "siege-shells-1",
    "Siege Shells I",
    "siege-guns",
    180,
    30,
    multiply("1.25", "siege-shield-damage", "siege-building-damage"),
  ),
  tech(
    "siege-shells-2",
    "Siege Shells II",
    "siege-shells-1",
    540,
    45,
    multiply("1.25", "siege-shield-damage", "siege-building-damage"),
  ),
  tech(
    "stronger-shields-1",
    "Stronger Shields I",
    null,
    100,
    25,
    multiply("1.25", "shield-recharge", "shield-capacity"),
  ),
  tech(
    "stronger-shields-2",
    "Stronger Shields II",
    "stronger-shields-1",
    300,
    40,
    multiply("1.25", "shield-recharge", "shield-capacity"),
  ),
  tech(
    "stronger-shields-3",
    "Stronger Shields III",
    "stronger-shields-2",
    900,
    60,
    multiply("1.25", "shield-recharge", "shield-capacity"),
  ),
  tech(
    "wider-supply-1",
    "Wider Supply I",
    null,
    120,
    25,
    multiply("1.25", "supply-depot-capacity"),
  ),
  tech(
    "wider-supply-2",
    "Wider Supply II",
    "wider-supply-1",
    360,
    40,
    multiply("1.25", "supply-depot-capacity"),
  ),
  tech(
    "faster-deployment-1",
    "Faster Deployment I",
    null,
    120,
    25,
    multiply("0.80", "military-travel-time"),
  ),
  tech(
    "faster-deployment-2",
    "Faster Deployment II",
    "faster-deployment-1",
    360,
    40,
    multiply("0.80", "military-travel-time"),
  ),
  tech("salvage-workshops", "Salvage Workshops", "construction-tools-1", 160, 30, {
    kind: "restore-metal-ratio",
    ratio: "0.35",
  }),
  tech(
    "industrial-armor-1",
    "Industrial Armor I",
    null,
    140,
    30,
    multiply("1.25", "non-hq-building-integrity"),
  ),
  tech(
    "industrial-armor-2",
    "Industrial Armor II",
    "industrial-armor-1",
    420,
    45,
    multiply("1.25", "non-hq-building-integrity"),
  ),
] as const satisfies readonly TechnologyDefinition[];

export type TechnologyId = (typeof TECHNOLOGIES)[number]["id"];

export const SPECIALIZATIONS = [
  specialization(
    "mining-town",
    "Mining Town",
    "industrial",
    ["factory-works"],
    800,
    60,
    multiply("1.50", "mining-rig-output"),
  ),
  specialization(
    "factory-town",
    "Factory Town",
    "industrial",
    ["factory-works"],
    800,
    60,
    multiply("1.50", "producer-factory-output", "producer-factory-metal-demand"),
  ),
  specialization(
    "university-town",
    "University Town",
    "industrial",
    ["factory-works"],
    800,
    60,
    multiply("1.50", "research-lab-output"),
  ),
  specialization(
    "border-guard",
    "Border Guard",
    "military",
    ["siege-guns", "robot-weapons-1"],
    500,
    45,
    multiply("1.35", "guard-troop-damage"),
  ),
  specialization(
    "raiding-force",
    "Raiding Force",
    "military",
    ["siege-guns", "robot-weapons-1"],
    500,
    45,
    { kind: "set-ratio", target: "raider-infrastructure-leak", ratio: "0.35" },
  ),
  specialization(
    "siege-regiment",
    "Siege Regiment",
    "military",
    ["siege-guns", "robot-weapons-1"],
    500,
    45,
    multiply("1.35", "siege-shield-damage", "siege-building-damage"),
  ),
] as const satisfies readonly SpecializationDefinition[];

export type SpecializationId = (typeof SPECIALIZATIONS)[number]["id"];

export const REPEATABLES = [
  repeatable("mining-output", "Mining Output", ["mining-rig-output"]),
  repeatable("construction-output", "Construction Output", ["construction-yard-output"]),
  repeatable("assembly-output", "Assembly Output", ["assembly-work"]),
  repeatable("shield-output", "Shield Output", ["shield-recharge", "shield-capacity"]),
] as const satisfies readonly RepeatableDefinition[];

export type RepeatableId = (typeof REPEATABLES)[number]["id"];
